import { useMemo } from 'react';
import { useAnalyticsContext } from '../../../../context/analytics-context';
import { useActiveCanvas } from '../../../../hooks';
import { overlayImageDefaultSettings } from '../../../../utils';
import { handleOrderChange } from '../utils/handle-order-change';
import { useHistoryTransaction, useHistoryTransactionErrorToast } from '../../../../features/edit-history/edit-history';

export const usePositionTab = () => {
  const canvas = useActiveCanvas();
  const runHistoryTransaction = useHistoryTransaction();
  const showHistoryTransactionError = useHistoryTransactionErrorToast();
  const { updateEditFormats } = useAnalyticsContext();
  const { lowestIndex, highestIndex } = useMemo(() => {
    if (canvas?.current) {
      const objects = canvas.current.getObjects();
      const overlayImgIndex = objects.findIndex((obj) => obj.name === overlayImageDefaultSettings.name);
      const foldLineIndex = objects.findIndex((obj) => obj?.data?.type === 'fold-line');
      const highestIndex = objects.length - 1;
      let lowestIndex;

      // If there is an overlayImage object, the text cannot go behind that object.
      if (overlayImgIndex !== -1) {
        lowestIndex = overlayImgIndex + 1;
      } else if (foldLineIndex !== -1) {
        // If there is no overlayImage object but there is an  image with data.type === "fold-line"
        // the text cannot go behind the "fold-line" object.
        lowestIndex = foldLineIndex + 1;
      }
      return { lowestIndex, highestIndex };
    }
    return { lowestIndex: 0, highestIndex: 0 };
  }, [canvas]);

  const handleBringToFrontClick = async () => {
    const targetCanvas = canvas?.current;
    if (!targetCanvas) return;
    const activeObject = targetCanvas.getActiveObject();
    if (!activeObject || targetCanvas.getObjects().indexOf(activeObject) >= highestIndex) return;

    try {
      await runHistoryTransaction(targetCanvas, () => handleOrderChange(highestIndex, targetCanvas));
      updateEditFormats({ order: 'Bring To Front' });
    } catch {
      showHistoryTransactionError();
    }
  };

  const handleSendToBackClick = async () => {
    const targetCanvas = canvas?.current;
    if (!targetCanvas) return;
    const activeObject = targetCanvas.getActiveObject();
    if (!activeObject || targetCanvas.getObjects().indexOf(activeObject) === lowestIndex) return;

    try {
      await runHistoryTransaction(targetCanvas, () => handleOrderChange(lowestIndex, targetCanvas));
      updateEditFormats({ order: 'Send To Back' });
    } catch {
      showHistoryTransactionError();
    }
  };

  return {
    handleBringToFrontClick,
    handleSendToBackClick,
  };
};
